import { replaceLogger } from './log-silencer' // oxfmt-ignore // must be imported before smartlib to silence its logs
import { LoggerManager, SmartLib } from '@broadpeak/smartlib' // oxfmt-ignore
import '@broadpeak/smartlib-ad' // oxfmt-ignore
import '@broadpeak/smartlib-analytics' // oxfmt-ignore
import '@broadpeak/smartlib-videojs' // oxfmt-ignore

import type { Src, TPlayer } from 'video.js'
import type { Logger } from '|>/shared/logger'
import type {
  AdBreakData,
  AdData,
  AdState,
  SmartlibPlugin,
  SmartlibPluginOptions,
} from './index.h'

import { VideoJsPlayerAdapter } from '@broadpeak/smartlib-videojs'
import once from 'lodash-es/once'
import videojs from 'video.js'
import { Events } from '|>/shared/events'
import { logger } from '|>/shared/logger'
import { Intent } from '|>/shared/types/intent'
import { EMPTY_AD_STATE } from './index.h'
import { formatAdBreakData, formatAdData } from './lib'

/**
 * SmartLib logger
 */
let __logger: Logger | undefined
const getLogger = () => __logger || (__logger = logger.new('smartlib'))
replaceLogger(getLogger)

/**
 * Initialize SmartLib library (should be called only once)
 */
const initLibrary = once(() => {
  VideoJsPlayerAdapter.attachLibrary(videojs)

  // Set default log level to 1 (verbose)
  LoggerManager.getInstance().setLogLevel(1)

  // Change default LogRedirectListener to use player logger
  LoggerManager.getInstance().setLogRedirectListener({
    onLog(line: string) {
      getLogger()?.debug(line)
    },
  })

  /**
   * Initialize SmartLib with default configuration
   *
   * @param analyticsAddress - Address of the Analytics Engine or empty string
   * @param nanoCDNHost - The nanoCDN Agent hostname or IP address or empty string
   * @param broadpeakDomainNames - The domain name list to identify Broadpeak products
   */
  SmartLib.getInstance().init('', '', 'stream.broadpeak.io')
})

// base plugin constructor
const BasePlugin: Src.Plugin = videojs.getPlugin('plugin') as Src.Plugin

/**
 * SmartLib VideoJS Plugin
 */
class SmartlibPluginClass extends BasePlugin implements SmartlibPlugin {
  private session: any = null
  private adState: AdState = { ...EMPTY_AD_STATE }
  private errorHandler: (() => void) | null = null
  private sourceChangeHandler: ((event: any, source: any) => void) | null = null
  private disposeHandler: (() => void) | null = null

  constructor(player: TPlayer, _options: SmartlibPluginOptions = {}) {
    super(player)

    // Initialize SmartLib library
    initLibrary()

    // Save handlers for proper cleanup
    this.sourceChangeHandler = this.handleSourceChange.bind(this)
    this.disposeHandler = this.dispose.bind(this)

    // Listen for source changes
    this.player.on(Events.Media.Load, this.sourceChangeHandler)

    // Cleanup on dispose
    this.player.on('dispose', this.disposeHandler)
  }

  /**
   * Handle source change
   */
  private handleSourceChange(_event: any, source: any) {
    if (source?.src) {
      this.startSession(source.src)
    } else {
      this.stopSession()
    }
  }

  /**
   * Start SmartLib session
   */
  private startSession(sourceUrl: string) {
    this.stopSession(true)

    // Reset ad state
    this.adState = { ...EMPTY_AD_STATE }
    this.triggerAdStateChange()

    // Create new streaming session
    this.session = SmartLib.getInstance().createStreamingSession()

    // Attach player to session
    this.session.attachPlayer(this.player)

    this.session.setAdDataListener({
      /**
       * Triggered when ad data have been received during getURL
       * @param adList list of ad breaks
       */
      onAdData: () => {},
    })

    // Set ad events listener
    this.session.setAdEventsListener({
      onAdBreakBegin: (adBreakData: AdBreakData) => {
        this.adState = {
          ...this.adState,
          isAdBreakActive: true,
          currentAdBreak: formatAdBreakData(adBreakData),
        }
        this.triggerAdStateChange()
        this.player.trigger(Events.User.Intent, Intent.StartAd)
      },

      onAdBegin: (adData: AdData, adBreakData: AdBreakData) => {
        this.adState = {
          ...this.adState,
          isAdPlaying: true,
          currentAd: formatAdData(adData),
          currentAdBreak: formatAdBreakData(adBreakData),
        }
        this.triggerAdStateChange()
      },

      onAdSkippable: (
        _adData: AdData,
        _adBreakData: AdBreakData,
        adSkippablePosition: number,
        adEndPosition: number,
        _adBreakEndPosition: number
      ) => {
        const isAdSkippable = adSkippablePosition < adEndPosition
        this.adState = {
          ...this.adState,
          isAdSkippable,
        }
        this.triggerAdStateChange()
      },

      onAdEnd: (_adData: AdData, _adBreakData: AdBreakData) => {
        this.adState = {
          ...this.adState,
          isAdPlaying: false,
          currentAd: undefined,
          isAdSkippable: false,
        }
        this.triggerAdStateChange()
      },

      onAdBreakEnd: (_adBreakData: AdBreakData) => {
        this.adState = { ...EMPTY_AD_STATE }
        this.triggerAdStateChange()
        this.player.trigger(Events.User.Intent, Intent.EndAd)
      },
    })

    // Activate advertising
    this.session.activateAdvertising()

    // Get URL with ad insertions
    this.session.getURL(sourceUrl).then((result: any) => {
      if (result.isError()) {
        this.stopSession()
      }
    })

    // Listen for errors
    this.errorHandler = () => {
      this.stopSession()
    }
    this.player.on('error', this.errorHandler)
  }

  /**
   * Stop SmartLib session
   */
  private stopSession(skipStateChange = false) {
    if (this.session) {
      this.session.stopStreamingSession()
      this.session.detachPlayer?.()
      this.session = null

      // Reset ad state
      this.adState = { ...EMPTY_AD_STATE }

      // Skip state change if player is being disposed
      if (!skipStateChange) {
        this.triggerAdStateChange()
      }
    }
  }

  /**
   * Trigger ad state change event
   */
  private triggerAdStateChange() {
    // Check if player is still available and not disposed
    if (this.player && !this.player.isDisposed()) {
      this.player.trigger(Events.Media.AdStateChanged, this.adState)
    }
  }

  /**
   * Get current ad state
   */
  getState(): AdState {
    return { ...this.adState }
  }

  /**
   * Skip current ad
   */
  skip() {
    const { currentAd } = this.adState
    if (currentAd && this.adState.isAdSkippable) {
      const skipToTime = currentAd.startPosition + currentAd.duration
      this.player.currentTime(skipToTime)
    }
  }

  /**
   * Dispose plugin
   */
  override dispose() {
    // Stop session without triggering state change (player may be disposed)
    this.stopSession(true)

    // Remove event listeners
    if (this.player && !this.player.isDisposed()) {
      if (this.sourceChangeHandler) {
        this.player.off(Events.Media.Load, this.sourceChangeHandler)
        this.sourceChangeHandler = null
      }
      if (this.disposeHandler) {
        this.player.off('dispose', this.disposeHandler)
        this.disposeHandler = null
      }
      if (this.errorHandler) {
        this.player.off('error', this.errorHandler)
        this.errorHandler = null
      }

      super.dispose()
    }
  }
}

// Register plugin
videojs.registerPlugin('smartlib', SmartlibPluginClass)
